import React from 'react';
import { useQuery, gql, useMutation } from '@apollo/client';
import {
    Card,
    CardContent,
    Grid,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
    Button,
    Snackbar,
    Alert,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router';
import { useState, useRef } from 'react';
import { Stack } from '@mui/system';
import { Backspace, Add, CloudUpload } from '@mui/icons-material';
import CorporatesRegCard from './CorporatesRegCard';
import CompanyImportDialog from './CompanyImportDialog';
import AddCompanyDialog from './AddCompanyDialog';
import UpgradeLimitDialog from './UpgradeLimitDialog';

const GET_CORPORATEREGISTERED_LIST = gql`
    query GetCorporatesRegisteredData($eventId: String!, $searchTerm: String) {
        getCorporatesRegisteredData(eventId: $eventId, searchTerm: $searchTerm)
    }
`;

const ADD_COMPANY = gql`
    mutation AddCompany($formDetail: String!, $role: String!) {
        addCompany(formDetail: $formDetail, role: $role) {
            status
            message
        }
    }
`;

const UPDATE_COMPANY = gql`
    mutation UpdateSponsor($id: String!, $allowedRegistrations: String, $allowed_no_of_staff: String, $role: String!) {
        updateSponsor(
            id: $id
            allowedRegistrations: $allowedRegistrations
            allowed_no_of_staff: $allowed_no_of_staff
            role: $role
        ) {
            status
            message
        }
    }
`;

function SearchBox({ onSearch }) {
    const [search, setSearch] = React.useState('');
    React.useEffect(() => {
        const t = setTimeout(() => onSearch(search), 500);
        return () => clearTimeout(t);
    }, [search]);
    return (
        <TextField
            sx={{ width: 300 }}
            label='Search here...'
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
                autoComplete: 'new-password',
                endAdornment: (
                    <InputAdornment position='end'>
                        {search && (
                            <IconButton onClick={() => setSearch('')}>
                                <Backspace />
                            </IconButton>
                        )}
                    </InputAdornment>
                ),
            }}
        />
    );
}

export default function CorporatesReg() {
    const IMPORT_COMPANIES = gql`
        mutation ImportCompanies($eventId: String!, $role: String!, $jsonData: String!) {
            importCompanies(eventId: $eventId, role: $role, jsonData: $jsonData) {
                total
                inserted
                duplicates
                failed
                failedRows
            }
        }
    `;
    const [importCompanies] = useMutation(IMPORT_COMPANIES);
    const fileInputRef = useRef(null);
    const [importOpen, setImportOpen] = useState(false);
    const downloadSampleCsv = () => {
        const headers = ['Email', 'Company', 'allowedRegistrations', 'allowed_no_of_staff', 'stallNo'];
        const sampleData = [
            ['corporate@example.com', 'Sample Corporate', '10', '5', 'Booth-001'],
            ['corporate2@example.com', 'Another Corporate', '15', '8', 'Booth-002'],
        ];
        const csvContent = [headers.join(','), ...sampleData.map(row => row.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `corporate-import-sample.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };
    const handleImportClick = () => fileInputRef.current?.click();
    const handleFile = async e => {
        const file = e.target.files?.[0];
        if (!file) return;
        const text = await file.text();
        const rows = text
            .split('\n')
            .filter(Boolean)
            .map(line => line.split(','));
        const [header, ...dataRows] = rows;
        const json = dataRows.map(cols =>
            Object.fromEntries(header.map((h, i) => [h.trim(), (cols[i] || '').replace(/^"|"$/g, '')]))
        );
        const res = await importCompanies({
            variables: { eventId, role: 'Corporates', jsonData: JSON.stringify(json) },
        });
        const report = res?.data?.importCompanies;
        if (report) {
            const failed = JSON.parse(report.failedRows || '[]');
            setSnackbar({
                open: true,
                message: `Imported: ${report.inserted}, Duplicates: ${report.duplicates}, Failed: ${report.failed}`,
                severity: report.failed ? 'warning' : 'success',
            });
            if (failed.length) {
                const csvString = [
                    ['reason', ...Object.keys(failed[0] || {})].join(','),
                    ...failed.map(r =>
                        [
                            r.reason,
                            ...Object.keys(r)
                                .filter(k => k !== 'reason')
                                .map(k => JSON.stringify(r[k] ?? '')),
                        ].join(',')
                    ),
                ].join('\n');
                const blob = new Blob([csvString], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${eventId}-Corporates-failed-import.csv`;
                a.click();
                URL.revokeObjectURL(url);
            }
        }
    };
    const { eventId } = useParams();
    const [searchTerm, setSearchTerm] = useState('');
    const role = 'Corporates';
    const { loading, error, data, refetch } = useQuery(GET_CORPORATEREGISTERED_LIST, {
        variables: { eventId, searchTerm },
    });
    const navigate = useNavigate();
    const [openDialog, setOpenDialog] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
    const [currentLimits, setCurrentLimits] = useState({ allowedRegistrations: 0, allowed_no_of_staff: 0 });

    const [addCompany] = useMutation(ADD_COMPANY);
    const [updateCorporate] = useMutation(UPDATE_COMPANY);

    const corporatesRegisteredData =
        (data && data?.getCorporatesRegisteredData && JSON.parse(data?.getCorporatesRegisteredData)) || [];

    const handleAddCorporate = async formData => {
        try {
            const response = await addCompany({
                variables: {
                    formDetail: JSON.stringify(formData),
                    role: 'Corporates',
                },
                refetchQueries: [
                    {
                        query: GET_CORPORATEREGISTERED_LIST,
                        variables: { eventId, searchTerm },
                    },
                ],
            });

            if (response.data.addCompany.status === true) {
                setSnackbar({
                    open: true,
                    message: response.data.addCompany.message,
                    severity: 'success',
                });
                setOpenDialog(false);
            } else {
                setSnackbar({
                    open: true,
                    message: response.data.addCompany.message,
                    severity: 'error',
                });
            }
        } catch (error) {
            setSnackbar({
                open: true,
                message: error.message,
                severity: 'error',
            });
        }
    };

    const handleUpdateLimit = async formData => {
        try {
            const response = await updateCorporate({
                variables: {
                    id: corporatesRegisteredData[0]?.corporateId,
                    allowedRegistrations: formData.allowedRegistrations.toString(),
                    allowed_no_of_staff: formData.allowed_no_of_staff.toString(),
                    role: 'Corporates',
                },
                refetchQueries: [
                    {
                        query: GET_CORPORATEREGISTERED_LIST,
                        variables: { eventId, searchTerm },
                    },
                ],
            });

            if (response.data.updateSponsor.status === true) {
                setSnackbar({
                    open: true,
                    message: response.data.updateSponsor.message,
                    severity: 'success',
                });
                setUpgradeDialogOpen(false);
            } else {
                setSnackbar({
                    open: true,
                    message: response.data.updateSponsor.message,
                    severity: 'error',
                });
            }
        } catch (error) {
            setSnackbar({
                open: true,
                message: error.message,
                severity: 'error',
            });
        }
    };

    return (
        <div>
            <Stack
                direction={'row'}
                justifyContent='space-between'
                alignItems='center'
                sx={{
                    width: '100%',
                    mb: 5,
                }}
            >
                <Typography variant='h1' textAlign={'center'}>
                    Corporates Data
                </Typography>
                <Stack direction='row' spacing={2} alignItems='center'>
                    <SearchBox onSearch={setSearchTerm} />
                    <Button variant='outlined' onClick={downloadSampleCsv}>
                        Download Sample CSV
                    </Button>
                    <Button
                        variant='outlined'
                        startIcon={<CloudUpload />}
                        onClick={() => navigate(`/${eventId}/showcorporates/import`)}
                    >
                        Import {role}
                    </Button>
                    <input
                        ref={fileInputRef}
                        type='file'
                        accept='.csv'
                        onChange={handleFile}
                        style={{ display: 'none' }}
                    />
                    <Button variant='contained' color='primary' startIcon={<Add />} onClick={() => setOpenDialog(true)}>
                        Add Corporate
                    </Button>
                </Stack>
            </Stack>
            <Grid container spacing={2} justifyContent='center'>
                {corporatesRegisteredData.length > 0 ? (
                    corporatesRegisteredData
                        ?.sort((a, b) => (a.company > b.company ? 1 : -1))
                        .map((corporateData, index) => (
                            <Grid item xs={12} sm={6} md={4} lg={4} xl={2} key={index}>
                                <CorporatesRegCard
                                    corporateData={corporateData}
                                    onUpdateLimit={data => {
                                        // Extract the allowed registration limits for VIP and Delegate
                                        const vipData = data.registrationData?.data?.find(item => item.name === 'vip');
                                        const delegateData = data.registrationData?.data?.find(
                                            item => item.name === 'delegate'
                                        );

                                        // Ensure values exist before setting state
                                        setCurrentLimits({
                                            allowedRegistrations: vipData ? vipData.allowedRegistration : 0,
                                            allowed_no_of_staff: delegateData ? delegateData.allowedRegistration : 0,
                                        });

                                        setUpgradeDialogOpen(true);
                                    }}
                                    onSendEmail={() => {
                                        // TODO: Implement email sending for corporates
                                        console.log('Send email to corporate:', corporateData);
                                    }}
                                />
                            </Grid>
                        ))
                ) : (
                    <Typography textAlign='center'>Oops! No data found</Typography>
                )}
            </Grid>

            <CompanyImportDialog
                open={importOpen}
                onClose={() => setImportOpen(false)}
                eventId={eventId}
                role={role}
                onReport={report => {
                    const failed = JSON.parse(report?.failedRows || '[]');
                    setSnackbar({
                        open: true,
                        message: `Imported: ${report.inserted}, Duplicates: ${report.duplicates}, Failed: ${report.failed}`,
                        severity: report.failed ? 'warning' : 'success',
                    });
                    if (failed.length) {
                        const csvString = [
                            ['reason', ...Object.keys(failed[0] || {})].join(','),
                            ...failed.map(r =>
                                [
                                    r.reason,
                                    ...Object.keys(r)
                                        .filter(k => k !== 'reason')
                                        .map(k => JSON.stringify(r[k] ?? '')),
                                ].join(',')
                            ),
                        ].join('\n');
                        const blob = new Blob([csvString], { type: 'text/csv' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `${eventId}-${role}-failed-import.csv`;
                        a.click();
                        URL.revokeObjectURL(url);
                    }
                }}
            />

            <AddCompanyDialog open={openDialog} onClose={() => setOpenDialog(false)} onSubmit={handleAddCorporate} />

            <UpgradeLimitDialog
                open={upgradeDialogOpen}
                onClose={() => setUpgradeDialogOpen(false)}
                onSubmit={handleUpdateLimit}
                currentLimits={currentLimits}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
}
